import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";

const faqs = [
  {
    id: "faq-1",
    question: "What is JIVI Solutions and what does it offer?",
    answer:
      "JIVI Solutions is a global leader in intelligent enterprise workforce planning and management software. It provides an end-to-end platform for scheduling, workload planning, automated allocation, and employee self-service.",
  },
  {
    id: "faq-2",
    question: "How does JIVI’s automated work allocation system work?",
    answer:
      "The system uses user-defined rules to automatically assign manpower resources to planned workloads. It supports multiple allocation scenarios and simplifies operations with one-click assignment.",
  },
  {
    id: "faq-3",
    question:
      "Can JIVI Solutions integrate with existing systems like TOS or time clocks?",
    answer:
      "Yes, JIVI Solutions is designed for seamless integration with terminal operating systems (TOS) for vessel, equipment, and labor scheduling, as well as time clock interfaces for attendance tracking.",
  },
  {
    id: "faq-4",
    question: "What industries can benefit from JIVI Solutions?",
    answer:
      "All data transmissions are encrypted, ensuring the confidentiality and security of sensitive information. JIVI is built to support complex workforce environments across various industries, including logistics, transportation, and enterprise operations that require precise scheduling and resource optimization.",
  },
  {
    id: "faq-5",
    question: "Does JIVI Solutions offer employee self-service features?",
    answer:
      "Absolutely. Employees can manage leave requests and view their work schedules online, improving transparency and reducing administrative overhead.",
  },
  {
    id: "faq-6",
    question: "Is there a trial or demo available for JIVI Solutions?",
    answer: (
      <span>
        Yes, JIVI Solutions offers a free trial and demo to help organizations
        explore its capabilities before committing.{" "}
        <Link to="/schedule-a-demo">
          <motion.span
            whileHover={{ scale: 1.05 }}
            className="text-[#0047FF] cursor-pointer inline-block"
          >
            Schedule a Demo
          </motion.span>
        </Link>
      </span>
    ),
  },
];

const HomeFAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleFAQ = (index) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  const leftFAQs = faqs.slice(0, 3);
  const rightFAQs = faqs.slice(3);

  return (
    <section className="w-full bg-[#F9FAFB] font-epilogue py-[clamp(50px,6vw,90px)]">
      <div className="max-w-[1400px] mx-auto px-[clamp(20px,5vw,60px)]">
        <motion.h2
          initial={{ opacity: 0, y: -40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          viewport={{ once: true }}
          className="text-center text-[#1D1D1D] text-[clamp(26px,2.3vw,38px)] font-bold mb-[clamp(40px,5vw,70px)]"
        >
          Frequently Asked Questions
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-[clamp(20px,3vw,40px)]">
          <div className="flex flex-col gap-6">
            {leftFAQs.map((faq, index) => (
              <FAQItem
                key={faq.id}
                faq={faq}
                isActive={activeIndex === index}
                onToggle={() => toggleFAQ(index)}
              />
            ))}
          </div>
          <div className="flex flex-col gap-6">
            {rightFAQs.map((faq, index) => {
              const actualIndex = index + leftFAQs.length;
              return (
                <FAQItem
                  key={faq.id}
                  faq={faq}
                  isActive={activeIndex === actualIndex}
                  onToggle={() => toggleFAQ(actualIndex)}
                />
              );
            })}
          </div>

        </div>
      </div>
    </section>
  );
};

const FAQItem = ({ faq, isActive, onToggle }) => (
  <motion.div
    layout
    className="bg-white rounded-[18px] border border-[#E5E7EB] shadow-[0_6px_14px_rgba(0,0,0,0.08)] overflow-hidden"
  >
    <button
      onClick={onToggle}
      className="w-full flex justify-between items-center text-left px-8 py-6"
    >
      <span className="font-semibold text-[#1D1D1D] text-[clamp(16px,1.3vw,20px)]">
        {faq.question}
      </span>

      <motion.span
        animate={{ rotate: isActive ? 180 : 0 }}
        transition={{ duration: 0.3 }}
        className="text-[#2563EB] text-[32px] font-bold leading-none"
      >
        {isActive ? "–" : "+"}
      </motion.span>
    </button>

    <AnimatePresence initial={false}>
      {isActive && (
        <motion.div
          layout
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="px-8 pb-6 text-[#555] text-[clamp(14px,1vw,16px)] leading-relaxed"
        >
          {faq.answer}
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

export default HomeFAQ;
